import { useCallback, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ModuleProgressionMode } from "@/domain/moduleProgressionMode";
import {
  apiGetModuleCompletionStatus,
  apiRetryModuleContentExercises,
  moduleCompletionQueryKey,
} from "@/services/student/module-completion.api";
import { moduleProgressQueryKey } from "@/services/student/module-progress.api";
import { useNavigate } from "react-router-dom";
import { recordUserBehaviorEvent } from "@/services/analytics/userBehaviorEvents.api";

/**
 * Hook para gerenciar status de conclusão de módulo (completion status API).
 */
export const useModuleCompletion = (moduleId: string | null) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: completionStatus,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: moduleId ? moduleCompletionQueryKey(moduleId) : ["module-completion", "null"],
    queryFn: () => {
      if (!moduleId) throw new Error("ModuleId is required");
      return apiGetModuleCompletionStatus(moduleId);
    },
    enabled: !!moduleId,
    staleTime: 0,
  });

  const navigateToNextModule = useCallback(() => {
    if (completionStatus?.nextModuleId) {
      navigate(`/student/exercises/${completionStatus.nextModuleId}`);
    } else {
      navigate("/student/dashboard");
    }
  }, [completionStatus?.nextModuleId, navigate]);

  const navigateToReview = useCallback(() => {
    if (moduleId) {
      navigate(`/student/exercises/${moduleId}`);
    }
  }, [moduleId, navigate]);

  const retryContentExercisesMutation = useMutation({
    mutationFn: () => {
      if (!moduleId) throw new Error("ModuleId is required");
      return apiRetryModuleContentExercises(moduleId);
    },
    onSuccess: async () => {
      if (!moduleId) return;
      await queryClient.invalidateQueries({
        queryKey: moduleCompletionQueryKey(moduleId),
      });
      await queryClient.invalidateQueries({
        queryKey: moduleProgressQueryKey(moduleId),
      });
      navigate(`/student/exercises/${moduleId}`, {
        state: { retryContentExercises: true },
      });
    },
  });

  const retryContentExercises = useCallback(() => {
    retryContentExercisesMutation.mutate();
  }, [retryContentExercisesMutation]);

  const firedModuleCompletedRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      moduleId &&
      completionStatus?.isCompleted &&
      firedModuleCompletedRef.current !== moduleId
    ) {
      firedModuleCompletedRef.current = moduleId;
      recordUserBehaviorEvent("ModuleCompleted", {
        moduleId,
        approved: String(completionStatus.isApproved),
        progressionMode: String(completionStatus.progressionMode),
      });
    }
  }, [moduleId, completionStatus]);

  return {
    completionStatus,
    isLoading,
    error,
    isCompleted: completionStatus?.isCompleted ?? false,
    isApproved: completionStatus?.isApproved ?? false,
    scorePercentage: completionStatus?.scorePercentage ?? 0,
    progressionMode:
      completionStatus?.progressionMode ?? ModuleProgressionMode.ExerciseOnly,
    nextModuleId: completionStatus?.nextModuleId ?? null,
    isNextModuleAvailable: completionStatus?.isNextModuleAvailable ?? false,
    refetch,
    navigateToNextModule,
    navigateToReview,
    retryContentExercises,
    isRetryingContentExercises: retryContentExercisesMutation.isPending,
    retryContentExercisesError: retryContentExercisesMutation.error,
  };
};

/**
 * Hook para uso em tela de conclusão (já sabe que está completo)
 */
export const useModuleCompletionScreen = (moduleId: string | null) => {
  const {
    completionStatus,
    isLoading,
    error,
    navigateToNextModule,
    navigateToReview,
    retryContentExercises,
    isRetryingContentExercises,
    retryContentExercisesError,
    refetch,
  } = useModuleCompletion(moduleId);

  const handleContinue = useCallback(() => {
    navigateToNextModule();
  }, [navigateToNextModule]);

  const handleReview = useCallback(() => {
    navigateToReview();
  }, [navigateToReview]);

  const handleRetryContentExercises = useCallback(() => {
    retryContentExercises();
  }, [retryContentExercises]);

  return {
    completionStatus,
    isLoading,
    error,
    isApproved: completionStatus?.isApproved ?? false,
    scorePercentage: completionStatus?.scorePercentage ?? 0,
    totalExercises: completionStatus?.totalExercises ?? 0,
    correctAnswers: completionStatus?.correctAnswers ?? 0,
    progressionMode:
      completionStatus?.progressionMode ?? ModuleProgressionMode.ExerciseOnly,
    nextModuleId: completionStatus?.nextModuleId ?? null,
    isNextModuleAvailable: completionStatus?.isNextModuleAvailable ?? false,
    refetch,
    handleContinue,
    handleReview,
    handleRetryContentExercises,
    isRetryingContentExercises,
    retryContentExercisesError,
  };
};
